/**
 * Entry point of Single page web apps
 */
'use strict'

import { upperFirst } from 'lodash-es'

/** **************** Import constants ******************/
import constants from '@/configuration/constants'
import storageKeysConstants from './configuration/storageKeys.constants'

/** **************** Import Vue ******************/
import Vue from 'vue'
import { mapGetters } from 'vuex'
import router from '@/router'
import store from '@/store'

/** **************** Import Vue kerlink ******************/
import vkFilters from 'vue-kerlink/dist/filters'
import vkUtils from 'vue-kerlink/dist/utils'
import vkValidators from 'vue-kerlink/dist/validators'

/** **************** Import API ******************/
import clientHTTP from '@/modules/clientHTTP'
import auth from '@/api/auth'
import WmcApi from '@/api'

/** **************** Import Vue plugins ******************/
import '@/filters'
import KerlinkUI from '@/components/ui'
import GridMixin from '@/components/ui/GridMixin'
import Utils from '@/modules/utils'
import VueFlatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/themes/material_blue.css'
import Notifications from 'vue-notification'
import VueParams from 'vue-params'
import VueI18Next from 'vue-i18next'
import VeeValidate from 'vee-validate'
import VImgFallback from 'v-img-fallback'

/** **************** Import Vue application ******************/
import App from '@/App'

/** **************** Import translations ******************/
import resBundle from '@/locale/index'

/** **************** Import CSS ******************/
// Bootstrap theme
import 'bootstrap-kerlink'
// Local style
import '@/scss/style.scss'

/** **************** Import icons ******************/
import 'iot-dashboard-icons/style.css'

/** **************** Use Vue plugins ******************/
Vue.use(vkFilters)
Vue.use(vkUtils)
Vue.use(vkValidators)
Vue.use(WmcApi)
Vue.use(KerlinkUI)
Vue.mixin(GridMixin)
Vue.use(Utils)
Vue.use(VueFlatPickr)
Vue.use(Notifications)
Vue.use(VueParams)
Vue.use(VueI18Next)
Vue.use(VeeValidate)
Vue.use(VImgFallback, { })

// Use mixin to define common getters to ALL components
// /!\ Use it CAREFULLY as it affect ALL components, including third party plugins /!\
Vue.mixin({
  // mix the getters into computed with object spread operator
  computed: {
    ...mapGetters(['isRole']),
    imgBaseUrl () { return process.env.BASE_URL + 'img/' }
  },

  // Update document title with translated route name
  created () {
    if (this.$options.titleKey) {
      const suffix = 'Wanesy Management Center'
      const title = upperFirst(this.$t(`title:${this.$options.titleKey}`))

      if (this.eui) document.title = `${this.eui} - ${title} - ${suffix}`
      else if (this.devEui) document.title = `${this.devEui} - ${title} - ${suffix}`
      else if (title) document.title = `${title} - ${suffix}`
      else document.title = suffix
    }
  }
})

/** **************** Init application environment ******************/
// First of all, compare the current build version to the user's local storage version
// Clear local storage if versions are different in order to reset saved configurations, just keep authToken to stay connected
if (_VERSION !== localStorage.getItem(storageKeysConstants.VERSION)) {
  const authToken = localStorage.getItem(storageKeysConstants.TOKEN)
  localStorage.clear()
  if (authToken) localStorage.setItem(storageKeysConstants.TOKEN, authToken)
  localStorage.setItem(storageKeysConstants.VERSION, _VERSION)
}

/** **************** Init application plugins ******************/

// Init translation plugin
Vue.params.i18nextLanguage = 'en'
i18next.init({
  lng: Vue.params.i18nextLanguage,
  fallbackLng: 'en',
  resources: resBundle,
  interpolation: {
    escapeValue: false,
    prefix: '__',
    suffix: '__'
  }
})

/** **************** Init ajax hooks ******************/
// Manage unauthorized/forbidden requests (401 & 403)
// No redirection for reset password request
clientHTTP.addResponseInterceptor(
  null,
  error => {
    if (
      (error.response.status === 401 || (error.response.status === 403 && error.response.data.code === 'SECURITY_USER_NOT_VALIDATED')) &&
       error.config.url.indexOf('resetPassword') === -1 &&
       error.config.url.indexOf('validateUser') === -1
    ) {
      const currentRoute = router.resolve(window.location.hash.substring(1))
      auth.logout(currentRoute.route.fullPath)
    }
  }
)

/** **************** Init application ******************/

// Set initial store values
store.commit('setConstants', constants)

Vue.config.productionTip = false

// Vue instance
new Vue({
  router,
  store,
  render: h => h(App)
}).$mount('#app')
